import React, { useState, useEffect } from 'react';
import api from '../api';

const VERTICALS = [
  { value: 'gaming', label: 'Gaming' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'cpg', label: 'CPG' },
  { value: 'foodbev', label: 'Food & Bev' },
  { value: 'qsr', label: 'QSR' },
  { value: 'auto', label: 'Auto' },
  { value: 'finance', label: 'Finance' },
  { value: 'retail', label: 'Retail' },
  { value: 'sports', label: 'Sports' },
  { value: 'tech', label: 'Tech' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' }
];

const PerformanceDataEditor = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Company stats
  const [companyStats, setCompanyStats] = useState({
    avg_engagement_rate: '',
    avg_ctr: '',
    avg_viewability: '',
    avg_brand_lift: '',
    avg_conversion_lift: '',
    benchmark_comparison: '',
    notable_clients: [],
    key_differentiators: [],
    liveguard_enabled: true,
    native_unblockable: true
  });
  const [newClient, setNewClient] = useState('');
  const [newDifferentiator, setNewDifferentiator] = useState('');
  
  // Case studies
  const [caseStudies, setCaseStudies] = useState([]);
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);
  const [caseStudyForm, setCaseStudyForm] = useState({
    vertical: 'retail',
    client_name: '',
    campaign_name: '',
    engagement_rate: '',
    ctr: '',
    viewability: '',
    brand_lift: '',
    conversion_lift: '',
    campaign_description: '',
    results_summary: '',
    can_name_client: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, casesRes] = await Promise.all([
        api.get('/performance-stats'),
        api.get('/case-studies')
      ]);
      
      if (statsRes.data.stats) {
        setCompanyStats({
          ...companyStats,
          ...statsRes.data.stats,
          notable_clients: statsRes.data.stats.notable_clients || [],
          key_differentiators: statsRes.data.stats.key_differentiators || []
        });
      }
      
      setCaseStudies(casesRes.data.caseStudies || []);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCompanyStats = async () => {
    setSaving(true);
    try {
      await api.put('/performance-stats', companyStats);
      alert('Company stats saved!');
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const addClient = () => {
    if (newClient.trim() && !companyStats.notable_clients.includes(newClient.trim())) {
      setCompanyStats({
        ...companyStats,
        notable_clients: [...companyStats.notable_clients, newClient.trim()]
      });
      setNewClient('');
    }
  };

  const removeClient = (client) => {
    setCompanyStats({
      ...companyStats,
      notable_clients: companyStats.notable_clients.filter(c => c !== client)
    });
  };

  const addDifferentiator = () => {
    if (newDifferentiator.trim() && !companyStats.key_differentiators.includes(newDifferentiator.trim())) {
      setCompanyStats({
        ...companyStats,
        key_differentiators: [...companyStats.key_differentiators, newDifferentiator.trim()]
      });
      setNewDifferentiator('');
    }
  };

  const removeDifferentiator = (diff) => {
    setCompanyStats({
      ...companyStats,
      key_differentiators: companyStats.key_differentiators.filter(d => d !== diff)
    });
  };

  const saveCaseStudy = async () => {
    setSaving(true);
    try {
      const payload = editingCaseStudy 
        ? { ...caseStudyForm, id: editingCaseStudy.id }
        : caseStudyForm;
      
      await api.post('/case-studies', payload);
      
      await fetchData();
      setEditingCaseStudy(null);
      setCaseStudyForm({
        vertical: 'retail',
        client_name: '',
        campaign_name: '',
        engagement_rate: '',
        ctr: '',
        viewability: '',
        brand_lift: '',
        conversion_lift: '',
        campaign_description: '',
        results_summary: '',
        can_name_client: true
      });
      alert('Case study saved!');
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const editCaseStudy = (cs) => {
    setEditingCaseStudy(cs);
    setCaseStudyForm({
      vertical: cs.vertical,
      client_name: cs.client_name,
      campaign_name: cs.campaign_name || '',
      engagement_rate: cs.engagement_rate || '',
      ctr: cs.ctr || '',
      viewability: cs.viewability || '',
      brand_lift: cs.brand_lift || '',
      conversion_lift: cs.conversion_lift || '',
      campaign_description: cs.campaign_description || '',
      results_summary: cs.results_summary || '',
      can_name_client: cs.can_name_client !== false
    });
    setActiveTab('cases');
  };

  const deleteCaseStudy = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    try {
      await api.delete(`/case-studies/${id}`);
      await fetchData();
    } catch (error) {
      alert('Failed to delete');
    }
  };

  if (loading) {
    return <div className="text-zinc-400 p-4">Loading performance data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2">
        <button
          onClick={() => setActiveTab('company')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeTab === 'company' 
              ? 'bg-zinc-800 text-white' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          Company Stats
        </button>
        <button
          onClick={() => setActiveTab('cases')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeTab === 'cases' 
              ? 'bg-zinc-800 text-white' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          Case Studies ({caseStudies.length})
        </button>
      </div>

      {/* Company Stats Tab */}
      {activeTab === 'company' && (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Enter your actual performance benchmarks. The AI will only cite these numbers instead of making up statistics.
          </p>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Avg Engagement Rate</label>
              <input
                type="text"
                value={companyStats.avg_engagement_rate}
                onChange={(e) => setCompanyStats({ ...companyStats, avg_engagement_rate: e.target.value })}
                placeholder="e.g., 3.2%"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Avg CTR</label>
              <input
                type="text"
                value={companyStats.avg_ctr}
                onChange={(e) => setCompanyStats({ ...companyStats, avg_ctr: e.target.value })}
                placeholder="e.g., 1.8%"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Avg Viewability</label>
              <input
                type="text"
                value={companyStats.avg_viewability}
                onChange={(e) => setCompanyStats({ ...companyStats, avg_viewability: e.target.value })}
                placeholder="e.g., 95%"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Avg Brand Lift</label>
              <input
                type="text"
                value={companyStats.avg_brand_lift}
                onChange={(e) => setCompanyStats({ ...companyStats, avg_brand_lift: e.target.value })}
                placeholder="e.g., +15%"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Avg Conversion Lift</label>
              <input
                type="text"
                value={companyStats.avg_conversion_lift}
                onChange={(e) => setCompanyStats({ ...companyStats, avg_conversion_lift: e.target.value })}
                placeholder="e.g., +22%"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">vs Benchmark</label>
              <input
                type="text"
                value={companyStats.benchmark_comparison}
                onChange={(e) => setCompanyStats({ ...companyStats, benchmark_comparison: e.target.value })}
                placeholder="e.g., 3x higher than traditional"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
            </div>
          </div>

          {/* Key Differentiators */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Key Differentiators</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {companyStats.key_differentiators.map((diff, i) => (
                <span key={i} className="bg-zinc-800 text-zinc-300 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  {diff}
                  <button onClick={() => removeDifferentiator(diff)} className="text-zinc-500 hover:text-red-400">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newDifferentiator}
                onChange={(e) => setNewDifferentiator(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addDifferentiator()}
                placeholder="e.g., Native, unblockable ads"
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
              <button onClick={addDifferentiator} className="bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-zinc-600">Add</button>
            </div>
          </div>

          {/* Notable Clients */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Clients You Can Name-Drop</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {companyStats.notable_clients.map((client, i) => (
                <span key={i} className="bg-nextide-turquoise/20 text-nextide-turquoise px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  {client}
                  <button onClick={() => removeClient(client)} className="text-nextide-turquoise/60 hover:text-red-400">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newClient}
                onChange={(e) => setNewClient(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addClient()}
                placeholder="e.g., Nike, Target, Wendy's"
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
              />
              <button onClick={addClient} className="bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-zinc-600">Add</button>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={companyStats.liveguard_enabled}
                onChange={(e) => setCompanyStats({ ...companyStats, liveguard_enabled: e.target.checked })}
                className="rounded bg-zinc-800 border-zinc-700 text-nextide-turquoise focus:ring-nextide-turquoise"
              />
              <span className="text-zinc-300 text-sm">Brand-safe with LiveGuard</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={companyStats.native_unblockable}
                onChange={(e) => setCompanyStats({ ...companyStats, native_unblockable: e.target.checked })}
                className="rounded bg-zinc-800 border-zinc-700 text-nextide-turquoise focus:ring-nextide-turquoise"
              />
              <span className="text-zinc-300 text-sm">Native & Unblockable</span>
            </label>
          </div>

          <button
            onClick={saveCompanyStats}
            disabled={saving}
            className="bg-nextide-turquoise text-black px-6 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Company Stats'}
          </button>
        </div>
      )}

      {/* Case Studies Tab */}
      {activeTab === 'cases' && (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Add case studies per vertical. The AI will reference these when emailing contacts in that industry.
          </p>

          {/* Case Study Form */}
          <div className="bg-zinc-800/50 rounded-xl p-4 space-y-4">
            <h3 className="text-white font-medium">
              {editingCaseStudy ? 'Edit Case Study' : 'Add New Case Study'}
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Vertical *</label>
                <select
                  value={caseStudyForm.vertical}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, vertical: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                >
                  {VERTICALS.map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Client Name *</label>
                <input
                  type="text"
                  value={caseStudyForm.client_name}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, client_name: e.target.value })}
                  placeholder="e.g., Target"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Campaign Name</label>
                <input
                  type="text"
                  value={caseStudyForm.campaign_name}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, campaign_name: e.target.value })}
                  placeholder="e.g., Back to School 2024"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={caseStudyForm.can_name_client}
                    onChange={(e) => setCaseStudyForm({ ...caseStudyForm, can_name_client: e.target.checked })}
                    className="rounded bg-zinc-800 border-zinc-700 text-nextide-turquoise focus:ring-nextide-turquoise"
                  />
                  <span className="text-zinc-300 text-sm">Can name client publicly</span>
                </label>
              </div>
            </div>

            {/* Results Metrics */}
            <div className="grid grid-cols-5 gap-3">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Engagement</label>
                <input
                  type="text"
                  value={caseStudyForm.engagement_rate}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, engagement_rate: e.target.value })}
                  placeholder="4.5%"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">CTR</label>
                <input
                  type="text"
                  value={caseStudyForm.ctr}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, ctr: e.target.value })}
                  placeholder="2.1%"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Viewability</label>
                <input
                  type="text"
                  value={caseStudyForm.viewability}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, viewability: e.target.value })}
                  placeholder="98%"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Brand Lift</label>
                <input
                  type="text"
                  value={caseStudyForm.brand_lift}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, brand_lift: e.target.value })}
                  placeholder="+18%"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Conv. Lift</label>
                <input
                  type="text"
                  value={caseStudyForm.conversion_lift}
                  onChange={(e) => setCaseStudyForm({ ...caseStudyForm, conversion_lift: e.target.value })}
                  placeholder="+25%"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-nextide-turquoise"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1">Results Summary</label>
              <textarea
                value={caseStudyForm.results_summary}
                onChange={(e) => setCaseStudyForm({ ...caseStudyForm, results_summary: e.target.value })}
                placeholder="Brief summary of results, e.g., 'Drove 50K store visits during back-to-school season'"
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise resize-none"
              />
            </div>

            <div className="flex gap-3">
              {editingCaseStudy && (
                <button
                  onClick={() => {
                    setEditingCaseStudy(null);
                    setCaseStudyForm({
                      vertical: 'retail',
                      client_name: '',
                      campaign_name: '',
                      engagement_rate: '',
                      ctr: '',
                      viewability: '',
                      brand_lift: '',
                      conversion_lift: '',
                      campaign_description: '',
                      results_summary: '',
                      can_name_client: true
                    });
                  }}
                  className="bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-zinc-600"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={saveCaseStudy}
                disabled={saving || !caseStudyForm.client_name}
                className="bg-nextide-turquoise text-black px-6 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Saving...' : (editingCaseStudy ? 'Update Case Study' : 'Add Case Study')}
              </button>
            </div>
          </div>

          {/* Existing Case Studies */}
          {caseStudies.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-white font-medium">Your Case Studies</h3>
              {VERTICALS.map(vertical => {
                const verticalCases = caseStudies.filter(cs => cs.vertical === vertical.value);
                if (verticalCases.length === 0) return null;
                
                return (
                  <div key={vertical.value} className="space-y-2">
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">{vertical.label}</div>
                    {verticalCases.map(cs => (
                      <div key={cs.id} className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <div className="text-white font-medium">
                            {cs.can_name_client ? cs.client_name : `${cs.client_name} (private)`}
                            {cs.campaign_name && <span className="text-zinc-400 font-normal"> — {cs.campaign_name}</span>}
                          </div>
                          <div className="text-zinc-400 text-sm">
                            {[
                              cs.engagement_rate && `${cs.engagement_rate} eng`,
                              cs.ctr && `${cs.ctr} CTR`,
                              cs.brand_lift && `${cs.brand_lift} brand lift`
                            ].filter(Boolean).join(' • ')}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editCaseStudy(cs)}
                            className="text-xs text-nextide-turquoise hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCaseStudy(cs.id)}
                            className="text-xs text-red-400 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PerformanceDataEditor;
